/**
 * Priority List Store (v2.2 Canon)
 *
 * Manages Priority Lists through their governance lifecycle:
 * Proposed → Decided → Ratified → Published (see types/prioritization/prioritySet.ts).
 *
 * The canonical PrioritySet type mirrors the `priority_sets` table 1:1 — the
 * decision/ratification/publication records map to the decided_*, ratified_*,
 * and published_* column groups, `overrides` is the JSONB column, and `events`
 * is loaded from the `governance_events` audit table (rows carrying this set's id).
 *
 * Direct ratification from `proposed` is allowed alongside the canonical
 * decided → ratified step: the production Catchball-acceptance RPCs
 * (catchball_accept_proposal, catchball_accept_output) ratify proposed sets in
 * one step, and the Ratification page mirrors that flow.
 *
 * Stakeholder Pulse integration (2026-04-28):
 * After a priority list is published, generatePulsesForPrioritySet() is
 * called fire-and-forget. Failures are logged but do not block publication.
 *
 * Per Terminology v2.2:
 * "Published Priority List (Artifact): Authoritative snapshot of priorities.
 * Created only by Ratified → Published governance event. Priority is a
 * contextual designation (membership in a Published list), not an Item State."
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import { getTenantContext } from '../../lib/tenantContext';
import { generatePulsesForPrioritySet } from '../../lib/stakeholderPulseService';
import {
  detectPrioritizationInversions,
  getUnacknowledgedInversions,
} from '../../lib/prioritizationInversionDetector';
import {
  loadInversionAcknowledgments,
  loadHardDependencyEdgesForItems,
} from '../../lib/inversionAcknowledgmentService';
import type {
  PrioritySet,
  PrioritySetState,
  GovernanceEvent,
  GovernanceEventType,
  PriorityOverride,
} from '../../types/prioritization/prioritySet';
import type { DependencySubtype } from '../../types/prioritization/itemRelationships';
import { isTerminalLoopState } from '../../lib/catchballLoopStates';
import { ratifyPriorityList } from '../../lib/priorityListRatification';

// Re-export the canonical types so existing `import { ... } from '.../prioritySetStore'`
// consumers keep working.
export type {
  PrioritySet,
  PrioritySetState,
  GovernanceEvent,
  PriorityOverride,
} from '../../types/prioritization/prioritySet';

// Get getSupabase() client lazily
const getSupabase = () => getSafeSupabaseClient();

/**
 * Like getSupabase() but throws when the client is unavailable.
 * Only call inside try/catch — every store action already catches and
 * records the error.
 */
function requireSupabase() {
  const client = getSafeSupabaseClient();
  if (!client) throw new Error('Supabase not available');
  return client;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Item priority context - where an item appears as a priority.
 */
export interface ItemPriorityContext {
  prioritySetId: string;
  prioritySetName: string;
  rank: number;
  aboveCutLine: boolean;
  publishedAt?: Date;
  cycleId: string;
  trackId?: string;
}

interface ActionResult {
  success: boolean;
  error?: string;
}

/**
 * Store state interface.
 */
interface PrioritySetStoreState {
  // Data
  prioritySets: PrioritySet[];
  prioritySetsByTrack: Map<string, PrioritySet[]>;
  prioritySetsByCycle: Map<string, PrioritySet[]>;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // CRUD Operations
  loadPrioritySetsForCycle: (cycleId: string) => Promise<void>;
  loadPrioritySetsForTrack: (trackId: string) => Promise<void>;
  createPrioritySet: (
    data: Partial<PrioritySet> & { cycleId: string; trackId: string; loopId: string; name: string }
  ) => Promise<PrioritySet | null>;
  updatePrioritySet: (id: string, updates: Partial<PrioritySet>) => Promise<boolean>;

  // Governance Operations (Proposed → Decided → Ratified → Published)
  decidePrioritySet: (
    id: string,
    actorId: string,
    opts?: { note?: string; conditions?: string[]; confidence?: 'high' | 'medium' | 'low' }
  ) => Promise<ActionResult>;
  ratifyPrioritySet: (id: string, actorId: string, opts?: { publishDate?: string; notes?: string }) => Promise<ActionResult>;
  publishPrioritySet: (id: string, actorId: string, message?: string) => Promise<ActionResult>;
  reopenPrioritySet: (id: string, actorId: string, note?: string) => Promise<ActionResult>;
  supersedePrioritySet: (id: string, newSetId: string, actorId: string) => Promise<ActionResult>;

  // Query Operations
  isItemPriority: (itemId: string, cycleId?: string) => boolean;
  getItemPriorityContexts: (itemId: string) => ItemPriorityContext[];
  getPublishedRanking: (trackId: string) => PrioritySet | null;
  getPublishedRankingForCycle: (cycleId: string) => PrioritySet[];
  getItemRank: (itemId: string, prioritySetId: string) => number | null;
  isAboveCutLine: (itemId: string, prioritySetId: string) => boolean;

  // Utilities
  getPrioritySetById: (id: string) => PrioritySet | undefined;
  clearCache: () => void;

  // Supabase sync (generic fetch for sync hooks)
  fetchFromSupabase: () => Promise<void>;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const usePrioritySetStore = create<PrioritySetStoreState>((set, get) => ({
  // Initial state
  prioritySets: [],
  prioritySetsByTrack: new Map(),
  prioritySetsByCycle: new Map(),
  isLoading: false,
  error: null,

  // =========================================================================
  // Load Operations
  // =========================================================================

  loadPrioritySetsForCycle: async (cycleId) => {
    try {
      set({ isLoading: true, error: null });

      const { data, error } = await requireSupabase()
        .from('priority_sets')
        .select('*')
        .eq('cycle_id', cycleId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const sets = await attachGovernanceEvents((data || []).map(mapDbRowToPrioritySet));

      set(state => {
        const byCycle = new Map(state.prioritySetsByCycle);
        byCycle.set(cycleId, sets);

        // Merge with existing sets
        const mergedSets = [
          ...state.prioritySets.filter(s => s.cycleId !== cycleId),
          ...sets,
        ];

        return { prioritySets: mergedSets, prioritySetsByCycle: byCycle, isLoading: false };
      });
    } catch (error) {
      console.error('Failed to load priority lists:', error);
      set({ isLoading: false, error: String(error) });
    }
  },

  loadPrioritySetsForTrack: async (trackId) => {
    try {
      set({ isLoading: true, error: null });

      const { data, error } = await requireSupabase()
        .from('priority_sets')
        .select('*')
        .eq('track_id', trackId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const sets = await attachGovernanceEvents((data || []).map(mapDbRowToPrioritySet));

      set(state => {
        const byTrack = new Map(state.prioritySetsByTrack);
        byTrack.set(trackId, sets);
        return { prioritySetsByTrack: byTrack, isLoading: false };
      });
    } catch (error) {
      console.error('Failed to load priority lists for track:', error);
      set({ isLoading: false, error: String(error) });
    }
  },

  // =========================================================================
  // CRUD Operations
  // =========================================================================

  createPrioritySet: async (data) => {
    try {
      const id = crypto.randomUUID();
      const now = new Date();

      const { data: result, error } = await requireSupabase()
        .from('priority_sets')
        .insert({
          id,
          cycle_id: data.cycleId,
          track_id: data.trackId,
          loop_id: data.loopId,
          name: data.name,
          description: data.description,
          state: data.state || 'proposed',
          item_pool_ids: data.itemPoolIds || [],
          item_ids: data.itemIds || [],
          ranked_item_ids: data.rankedItemIds || [],
          cut_line_position: data.cutLinePosition,
          proposed_at: now.toISOString(),
          proposed_by: data.proposedBy,
          workspace_id: getTenantContext()?.workspaceId ?? null,
          created_at: now.toISOString(),
          updated_at: now.toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      const prioritySet = mapDbRowToPrioritySet(result);

      set(state => ({
        prioritySets: [...state.prioritySets, prioritySet],
      }));

      return prioritySet;
    } catch (error) {
      console.error('Failed to create priority list:', error);
      set({ error: String(error) });
      return null;
    }
  },

  updatePrioritySet: async (id, updates) => {
    try {
      const dbUpdates: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.state !== undefined) dbUpdates.state = updates.state;
      if (updates.itemPoolIds !== undefined) dbUpdates.item_pool_ids = updates.itemPoolIds;
      if (updates.itemIds !== undefined) dbUpdates.item_ids = updates.itemIds;
      if (updates.rankedItemIds !== undefined) dbUpdates.ranked_item_ids = updates.rankedItemIds;
      if (updates.cutLinePosition !== undefined) dbUpdates.cut_line_position = updates.cutLinePosition;

      const { error } = await requireSupabase()
        .from('priority_sets')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        prioritySets: state.prioritySets.map(s =>
          s.id === id ? { ...s, ...updates, updatedAt: new Date() } : s
        ),
      }));

      return true;
    } catch (error) {
      console.error('Failed to update priority list:', error);
      set({ error: String(error) });
      return false;
    }
  },

  // =========================================================================
  // Governance Operations
  // =========================================================================

  /**
   * Decide a priority list (Proposed → Decided).
   * Per Canon INVARIANT 2: Decided = "green light for planning", not final.
   */
  decidePrioritySet: async (id, actorId, opts) => {
    try {
      const prioritySet = get().getPrioritySetById(id);
      if (!prioritySet) {
        return { success: false, error: 'Priority list not found' };
      }

      if (prioritySet.state !== 'proposed') {
        return { success: false, error: 'Priority list must be in proposed state to decide' };
      }

      const now = new Date();

      const { error } = await requireSupabase()
        .from('priority_sets')
        .update({
          state: 'decided',
          decided_at: now.toISOString(),
          decided_by: actorId,
          decision_note: opts?.note ?? null,
          decision_conditions: opts?.conditions ?? null,
          decision_confidence: opts?.confidence ?? null,
          updated_at: now.toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      const event = await insertGovernanceEvent({
        eventType: 'PRIORITY_SET_DECIDED',
        prioritySetId: id,
        cycleId: prioritySet.cycleId,
        actorId,
        occurredAt: now,
        fromState: 'proposed',
        toState: 'decided',
        note: opts?.note,
      });

      set(state => ({
        prioritySets: state.prioritySets.map(s =>
          s.id === id ? {
            ...s,
            state: 'decided' as PrioritySetState,
            decision: {
              decidedBy: actorId,
              decidedAt: now,
              note: opts?.note,
              conditions: opts?.conditions,
              confidence: opts?.confidence,
            },
            events: event ? [...s.events, event] : s.events,
            updatedAt: now,
          } : s
        ),
      }));

      return { success: true };
    } catch (error) {
      console.error('Failed to decide priority list:', error);
      return { success: false, error: String(error) };
    }
  },

  /**
   * Ratify a priority list (Decided → Ratified, or Proposed → Ratified).
   * Per Canon: Ratification is a prerequisite to publishing. Direct
   * ratification from proposed matches the Catchball-acceptance RPC path.
   *
   * The act is `ratify_priority_list` (migration 987): it checks today's rule
   * and the cycle's "Items / selection" decision right, and writes the list
   * and its PRIORITY_SET_RATIFIED event in one transaction. A direct write can
   * no longer make a list ratified. `actorId` is kept for the local record;
   * the server acts as the signed-in person.
   */
  ratifyPrioritySet: async (id, actorId, opts) => {
    try {
      const prioritySet = get().getPrioritySetById(id);
      if (!prioritySet) {
        return { success: false, error: 'Priority list not found' };
      }

      if (prioritySet.state !== 'proposed' && prioritySet.state !== 'decided') {
        return { success: false, error: 'Priority list must be in proposed or decided state to ratify' };
      }

      // No scheduled_publish_date column exists on priority_sets — the chosen
      // publish date is recorded on the governance event (the RPC writes it).
      const eventNote = [
        opts?.notes,
        opts?.publishDate ? `Scheduled publish date: ${opts.publishDate}` : null,
      ].filter(Boolean).join(' — ') || undefined;

      const result = await ratifyPriorityList(id, { note: opts?.notes ?? null, publishDate: opts?.publishDate ?? null });
      if (!result.ok) {
        return { success: false, error: result.error };
      }
      const now = new Date(result.data.ratifiedAt);
      const ratifiedBy = result.data.ratifiedBy || actorId;
      const event: GovernanceEvent | null = result.data.governanceEventId
        ? {
            id: result.data.governanceEventId,
            prioritySetId: id,
            type: normalizeEventType('PRIORITY_SET_RATIFIED'),
            actorId: ratifiedBy,
            occurredAt: now,
            note: eventNote,
            fromState: prioritySet.state,
            toState: 'ratified',
          }
        : null;

      set(state => ({
        prioritySets: state.prioritySets.map(s =>
          s.id === id ? {
            ...s,
            state: 'ratified' as PrioritySetState,
            ratification: {
              ratifiedBy,
              ratifiedAt: now,
              note: opts?.notes,
              overrides: s.ratification?.overrides ?? [],
            },
            events: event ? [...s.events, event] : s.events,
            updatedAt: now,
          } : s
        ),
      }));

      return { success: true };
    } catch (error) {
      console.error('Failed to ratify priority list:', error);
      return { success: false, error: String(error) };
    }
  },

  /**
   * Publish a priority list.
   * Per Canon: "Published Priority List is created only by Ratified → Published governance event."
   * Per Window 4 Invariant 5: Cannot publish unless loop is closed, EGAp iteration is accepted, no open revision_requests.
   */
  publishPrioritySet: async (id, actorId, message) => {
    try {
      const prioritySet = get().getPrioritySetById(id);
      if (!prioritySet) {
        return { success: false, error: 'Priority list not found' };
      }

      if (prioritySet.state !== 'ratified') {
        return { success: false, error: 'Priority list must be ratified before publishing' };
      }

      // Per Window 4 Invariant 5: Validate loop closure and iteration acceptance
      const supabase = getSupabase();
      if (prioritySet.loopId && supabase) {
        // Check loop state
        const { data: loop, error: loopError } = await supabase
          .from('catchball_loops')
          .select('state')
          .eq('id', prioritySet.loopId)
          .single();

        if (loopError) {
          console.error('[PrioritySetStore] Error checking loop:', loopError);
          return { success: false, error: 'Failed to verify loop state' };
        }

        if (!isTerminalLoopState(loop?.state)) {
          return {
            success: false,
            error: `Cannot publish: loop must be closed (current state: ${loop?.state})`,
          };
        }

        // Check for open revision_requests
        const { data: openRevisions, error: revError } = await supabase
          .from('revision_requests')
          .select('id')
          .eq('loop_id', prioritySet.loopId)
          .eq('status', 'open')
          .limit(1);

        if (revError) {
          console.warn('[PrioritySetStore] Error checking revision_requests:', revError);
        } else if (openRevisions && openRevisions.length > 0) {
          return {
            success: false,
            error: 'Cannot publish: open revision requests exist for this loop',
          };
        }

        // Check EGAp iteration status if associated
        const { data: iterations, error: iterError } = await supabase
          .from('egap_iterations')
          .select('id, status')
          .eq('catchball_loop_id', prioritySet.loopId)
          .order('iteration_index', { ascending: false })
          .limit(1);

        if (iterError) {
          console.warn('[PrioritySetStore] Error checking iteration:', iterError);
        } else if (iterations && iterations.length > 0) {
          const latestIteration = iterations[0];
          if (latestIteration.status !== 'accepted') {
            return {
              success: false,
              error: `Cannot publish: EGAp iteration must be accepted (current status: ${latestIteration.status})`,
            };
          }
        }
      }

      // -----------------------------------------------------------------------
      // Prioritization Inversion gate (canon: "Prioritization Inversion")
      //
      // Detect hard-dep inversions in the ranked set and block publication
      // if any lack acknowledgments. This is a prioritization-stage check —
      // distinct from planning-stage Rank–Sequence Divergence (§4.4).
      // -----------------------------------------------------------------------
      if (prioritySet.rankedItemIds.length > 0) {
        const rawEdges = await loadHardDependencyEdgesForItems(prioritySet.rankedItemIds);

        // Shape raw rows into the minimal edge form the detector needs
        const edges = rawEdges.map((e) => ({
          id: e.id,
          type: e.type,
          // The strength as stored. The detector reads it with one rule for both
          // assertion directions (isHardSequenceEdge); an edge with no stated
          // strength never gates, and migration 1004 stores none.
          subtype: (e.subtype ?? undefined) as DependencySubtype | undefined,
          directionality: 'directed' as const,
          status: 'active' as const,
          declaredMode: 'declared' as const,
          sourceItemId: e.sourceItemId,
          targetItemId: e.targetItemId,
          createdAt: new Date(),
        }));

        const inversions = detectPrioritizationInversions(prioritySet.rankedItemIds, edges);

        if (inversions.length > 0) {
          const acknowledgments = await loadInversionAcknowledgments(id);
          const unacked = getUnacknowledgedInversions(inversions, acknowledgments);

          if (unacked.length > 0) {
            return {
              success: false,
              error: `Cannot publish: ${unacked.length} prioritization inversion${unacked.length > 1 ? 's' : ''} require acknowledgment. Review the ranked list and acknowledge each inversion before publishing.`,
            };
          }
        }
      }
      // -----------------------------------------------------------------------

      const now = new Date();

      const { error } = await requireSupabase()
        .from('priority_sets')
        .update({
          state: 'published',
          published_at: now.toISOString(),
          published_by: actorId,
          publication_message: message,
          updated_at: now.toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      const event = await insertGovernanceEvent({
        eventType: 'PRIORITY_SET_PUBLISHED',
        prioritySetId: id,
        cycleId: prioritySet.cycleId,
        actorId,
        occurredAt: now,
        fromState: 'ratified',
        toState: 'published',
        note: message,
        afterStateSnapshot: {
          rankedItemIds: prioritySet.rankedItemIds,
          cutLinePosition: prioritySet.cutLinePosition,
        },
      });

      set(state => ({
        prioritySets: state.prioritySets.map(s =>
          s.id === id ? {
            ...s,
            state: 'published' as PrioritySetState,
            publication: {
              publishedBy: actorId,
              publishedAt: now,
              message,
            },
            events: event ? [...s.events, event] : s.events,
            updatedAt: now,
          } : s
        ),
      }));

      // Fire-and-forget: generate stakeholder pulse records for each item's submitter.
      // Failures are logged but must not block publication.
      generatePulsesForPrioritySet({
        prioritySetId: id,
        prioritySetName: prioritySet.name,
        rankedItemIds: prioritySet.rankedItemIds,
        cutLinePosition: prioritySet.cutLinePosition,
        publicationMessage: message,
        createdBy: actorId,
      }).catch(err =>
        console.warn('[PrioritySetStore] Stakeholder pulse generation failed (non-blocking):', err),
      );

      return { success: true };
    } catch (error) {
      console.error('Failed to publish priority list:', error);
      return { success: false, error: String(error) };
    }
  },

  /**
   * Reopen a decided priority list for revision (Decided → Proposed).
   * Clears the decision record — the set returns to the pre-decision state.
   */
  reopenPrioritySet: async (id, actorId, note) => {
    try {
      const prioritySet = get().getPrioritySetById(id);
      if (!prioritySet) {
        return { success: false, error: 'Priority list not found' };
      }

      if (prioritySet.state !== 'decided') {
        return { success: false, error: 'Only decided priority lists can be reopened' };
      }

      const now = new Date();

      const { error } = await requireSupabase()
        .from('priority_sets')
        .update({
          state: 'proposed',
          decided_at: null,
          decided_by: null,
          decision_note: null,
          decision_conditions: null,
          decision_confidence: null,
          updated_at: now.toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      const event = await insertGovernanceEvent({
        eventType: 'PRIORITY_SET_REOPENED',
        prioritySetId: id,
        cycleId: prioritySet.cycleId,
        actorId,
        occurredAt: now,
        fromState: 'decided',
        toState: 'proposed',
        note,
      });

      set(state => ({
        prioritySets: state.prioritySets.map(s =>
          s.id === id ? {
            ...s,
            state: 'proposed' as PrioritySetState,
            decision: undefined,
            events: event ? [...s.events, event] : s.events,
            updatedAt: now,
          } : s
        ),
      }));

      return { success: true };
    } catch (error) {
      console.error('Failed to reopen priority list:', error);
      return { success: false, error: String(error) };
    }
  },

  /**
   * Supersede a priority list with a new one.
   */
  supersedePrioritySet: async (id, newSetId, actorId) => {
    try {
      const prioritySet = get().getPrioritySetById(id);
      const now = new Date();

      const { error } = await requireSupabase()
        .from('priority_sets')
        .update({
          state: 'superseded',
          updated_at: now.toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      const event = await insertGovernanceEvent({
        eventType: 'PRIORITY_SET_SUPERSEDED',
        prioritySetId: id,
        cycleId: prioritySet?.cycleId,
        actorId,
        occurredAt: now,
        fromState: prioritySet?.state,
        toState: 'superseded',
        note: newSetId ? `Superseded by priority list ${newSetId}` : undefined,
      });

      set(state => ({
        prioritySets: state.prioritySets.map(s =>
          s.id === id ? {
            ...s,
            state: 'superseded' as PrioritySetState,
            events: event ? [...s.events, event] : s.events,
            updatedAt: now,
          } : s
        ),
      }));

      return { success: true };
    } catch (error) {
      console.error('Failed to supersede priority list:', error);
      return { success: false, error: String(error) };
    }
  },

  // =========================================================================
  // Query Operations
  // =========================================================================

  /**
   * Check if an item is a priority (appears in any published priority list).
   * Per Canon: "Priority is a contextual designation (membership in a Published list), not an Item State."
   */
  isItemPriority: (itemId, cycleId) => {
    const sets = get().prioritySets.filter(s =>
      s.state === 'published' &&
      s.rankedItemIds.includes(itemId) &&
      (cycleId ? s.cycleId === cycleId : true)
    );
    return sets.length > 0;
  },

  /**
   * Get all priority contexts for an item.
   */
  getItemPriorityContexts: (itemId) => {
    return get().prioritySets
      .filter(s => s.state === 'published' && s.rankedItemIds.includes(itemId))
      .map(s => {
        const rank = s.rankedItemIds.indexOf(itemId) + 1;
        return {
          prioritySetId: s.id,
          prioritySetName: s.name,
          rank,
          aboveCutLine: s.cutLinePosition ? rank <= s.cutLinePosition : true,
          publishedAt: s.publication?.publishedAt,
          cycleId: s.cycleId,
          trackId: s.trackId,
        };
      });
  },

  /**
   * Get the published ranking for a track (most recent published set).
   */
  getPublishedRanking: (trackId) => {
    const sets = get().prioritySetsByTrack.get(trackId) || [];
    return sets.find(s => s.state === 'published') || null;
  },

  /**
   * Get all published rankings for a cycle.
   */
  getPublishedRankingForCycle: (cycleId) => {
    const sets = get().prioritySetsByCycle.get(cycleId) || [];
    return sets.filter(s => s.state === 'published');
  },

  /**
   * Get an item's rank within a priority list.
   */
  getItemRank: (itemId, prioritySetId) => {
    const set = get().getPrioritySetById(prioritySetId);
    if (!set) return null;

    const index = set.rankedItemIds.indexOf(itemId);
    return index >= 0 ? index + 1 : null;
  },

  /**
   * Check if an item is above the cut line in a priority list.
   */
  isAboveCutLine: (itemId, prioritySetId) => {
    const set = get().getPrioritySetById(prioritySetId);
    if (!set || !set.cutLinePosition) return true;

    const rank = get().getItemRank(itemId, prioritySetId);
    return rank !== null && rank <= set.cutLinePosition;
  },

  // =========================================================================
  // Utilities
  // =========================================================================

  getPrioritySetById: (id) => {
    return get().prioritySets.find(s => s.id === id);
  },

  clearCache: () => {
    set({
      prioritySets: [],
      prioritySetsByTrack: new Map(),
      prioritySetsByCycle: new Map(),
    });
  },

  fetchFromSupabase: async () => {
    const supabase = getSupabase();
    if (!supabase) {
      console.log('[PrioritySetStore] Supabase not available');
      return;
    }

    try {
      set({ isLoading: true, error: null });

      const { data, error } = await supabase
        .from('priority_sets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const sets = await attachGovernanceEvents((data || []).map(mapDbRowToPrioritySet));

      set(_state => {
        const newByCycle = new Map<string, PrioritySet[]>();
        const newByTrack = new Map<string, PrioritySet[]>();

        for (const ps of sets) {
          if (ps.cycleId) {
            const existing = newByCycle.get(ps.cycleId) || [];
            newByCycle.set(ps.cycleId, [...existing, ps]);
          }
          if (ps.trackId) {
            const existing = newByTrack.get(ps.trackId) || [];
            newByTrack.set(ps.trackId, [...existing, ps]);
          }
        }

        return {
          prioritySets: sets,
          prioritySetsByCycle: newByCycle,
          prioritySetsByTrack: newByTrack,
          isLoading: false,
        };
      });

      console.log(`[PrioritySetStore] Loaded ${sets.length} priority lists from Supabase`);
    } catch (error) {
      console.error('[PrioritySetStore] Failed to fetch from Supabase:', error);
      set({ error: String(error), isLoading: false });
    }
  },
}));

// ============================================================================
// Helpers
// ============================================================================

function mapDbRowToPrioritySet(row: Record<string, unknown>): PrioritySet {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    trackId: row.track_id as string,
    loopId: (row.loop_id as string | null) ?? null,
    sessionId: (row.session_id as string | null) ?? null,
    name: row.name as string,
    description: (row.description as string | null) ?? undefined,
    state: (row.state as PrioritySetState) || 'proposed',
    itemPoolIds: (row.item_pool_ids as string[] | null) ?? [],
    itemIds: (row.item_ids as string[] | null) ?? [],
    rankedItemIds: (row.ranked_item_ids as string[] | null) ?? [],
    cutLinePosition: (row.cut_line_position as number | null) ?? undefined,
    proposedAt: new Date((row.proposed_at ?? row.created_at) as string),
    proposedBy: (row.proposed_by as string | null) ?? undefined,
    decision: row.decided_at ? {
      decidedBy: (row.decided_by as string | null) ?? 'unknown',
      decidedAt: new Date(row.decided_at as string),
      note: (row.decision_note as string | null) ?? undefined,
      conditions: (row.decision_conditions as string[] | null) ?? undefined,
      confidence: (row.decision_confidence as 'high' | 'medium' | 'low' | null) ?? undefined,
    } : undefined,
    ratification: row.ratified_at ? {
      ratifiedBy: (row.ratified_by as string | null) ?? 'unknown',
      ratifiedAt: new Date(row.ratified_at as string),
      note: (row.ratification_note as string | null) ?? undefined,
      overrides: parseOverrides(row.overrides),
    } : undefined,
    publication: row.published_at ? {
      publishedBy: (row.published_by as string | null) ?? 'unknown',
      publishedAt: new Date(row.published_at as string),
      message: (row.publication_message as string | null) ?? undefined,
      notifiedUserIds: (row.notified_user_ids as string[] | null) ?? undefined,
      externalUrl: (row.external_url as string | null) ?? undefined,
    } : undefined,
    events: [],
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

/** Revive the overrides JSONB column into PriorityOverride[] (dates included). */
function parseOverrides(raw: unknown): PriorityOverride[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((o): o is Record<string, unknown> => typeof o === 'object' && o !== null)
    .map(o => ({
      ...(o as unknown as PriorityOverride),
      overriddenAt: new Date(o.overriddenAt as string),
    }));
}

/**
 * DB event_type values → canonical GovernanceEventType.
 * The store and RPCs write SCREAMING_SNAKE types (per types/prioritization/
 * governanceEvent.ts); rows predating migration 069 may carry the original
 * lowercase values, which already match the canonical union.
 */
const EVENT_TYPE_ALIASES: Record<string, GovernanceEventType> = {
  PRIORITY_SET_PROPOSED: 'created',
  PRIORITY_SET_CREATED: 'created',
  PRIORITY_SET_DECIDED: 'decided',
  PRIORITY_SET_RATIFIED: 'ratified',
  PRIORITY_SET_PUBLISHED: 'published',
  PRIORITY_SET_SUPERSEDED: 'superseded',
  PRIORITY_SET_REOPENED: 'reopened',
  OVERRIDE_APPLIED: 'override_added',
  RANK_OVERRIDE: 'override_added',
};

function normalizeEventType(raw: string): GovernanceEvent['type'] {
  return EVENT_TYPE_ALIASES[raw] ?? raw;
}

function mapDbRowToGovernanceEvent(row: Record<string, unknown>): GovernanceEvent {
  return {
    id: row.id as string,
    prioritySetId: row.priority_set_id as string,
    type: normalizeEventType(row.event_type as string),
    actorId: (row.actor_id as string | null) ?? 'unknown',
    actorName: (row.actor_name as string | null) ?? undefined,
    occurredAt: new Date(row.occurred_at as string),
    note: (row.note as string | null) ?? undefined,
    fromState: (row.from_state as PrioritySetState | null) ?? undefined,
    toState: (row.to_state as PrioritySetState | null) ?? undefined,
    overrideId: (row.override_id as string | null) ?? undefined,
  };
}

/**
 * Load each set's audit trail from governance_events and attach it.
 * Event-load failures are non-fatal: sets are returned with empty histories.
 */
async function attachGovernanceEvents(sets: PrioritySet[]): Promise<PrioritySet[]> {
  if (sets.length === 0) return sets;

  try {
    const { data, error } = await requireSupabase()
      .from('governance_events')
      .select('*')
      .in('priority_set_id', sets.map(s => s.id))
      .order('occurred_at', { ascending: true });

    if (error) throw error;

    const eventsBySet = new Map<string, GovernanceEvent[]>();
    for (const row of data || []) {
      const event = mapDbRowToGovernanceEvent(row);
      const existing = eventsBySet.get(event.prioritySetId);
      if (existing) {
        existing.push(event);
      } else {
        eventsBySet.set(event.prioritySetId, [event]);
      }
    }

    return sets.map(s => ({ ...s, events: eventsBySet.get(s.id) ?? [] }));
  } catch (error) {
    console.warn('[PrioritySetStore] Failed to load governance events (non-blocking):', error);
    return sets;
  }
}

/**
 * Insert a governance_events audit row and return it in canonical form.
 * Failures are logged but never block the governance transition itself —
 * the priority_sets row is the state of record; events are the trail.
 */
async function insertGovernanceEvent(params: {
  eventType: string;
  prioritySetId: string;
  cycleId?: string;
  actorId: string;
  occurredAt: Date;
  fromState?: PrioritySetState;
  toState?: PrioritySetState;
  note?: string;
  afterStateSnapshot?: Record<string, unknown>;
}): Promise<GovernanceEvent | null> {
  const id = crypto.randomUUID();
  try {
    const { error } = await requireSupabase().from('governance_events').insert({
      id,
      event_type: params.eventType,
      occurred_at: params.occurredAt.toISOString(),
      actor_id: params.actorId,
      cycle_id: params.cycleId ?? null,
      priority_set_id: params.prioritySetId,
      from_state: params.fromState ?? null,
      to_state: params.toState ?? null,
      note: params.note ?? null,
      after_state_snapshot: params.afterStateSnapshot ?? null,
      workspace_id: getTenantContext()?.workspaceId ?? null,
    });

    if (error) throw error;

    return {
      id,
      prioritySetId: params.prioritySetId,
      type: normalizeEventType(params.eventType),
      actorId: params.actorId,
      occurredAt: params.occurredAt,
      note: params.note,
      fromState: params.fromState,
      toState: params.toState,
    };
  } catch (error) {
    console.warn('[PrioritySetStore] Failed to record governance event (non-blocking):', error);
    return null;
  }
}

// ============================================================================
// Selectors
// ============================================================================

export const selectPublishedSets = (state: PrioritySetStoreState) =>
  state.prioritySets.filter(s => s.state === 'published');

export const selectProposedSets = (state: PrioritySetStoreState) =>
  state.prioritySets.filter(s => s.state === 'proposed');

export const selectIsLoading = (state: PrioritySetStoreState) => state.isLoading;

export const selectError = (state: PrioritySetStoreState) => state.error;
